import { usagePct } from './format';
import { NEAR_LIMIT_AT, WARN_AT } from './constants';
import type { AiBudget } from './types';

/**
 * Monthly AI allowance readout (docs/13 D5). Deliberately two pieces: the meter is
 * transparency and the notices are consequences, and the side panel shows only the
 * second until usage actually matters (docs/24 P11).
 */

const cls = {
  meterWrap: 'mb-3',
  meterRow:  'flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.08em] text-slate mb-1',
  meterTrack:'h-1.5 w-full overflow-hidden rounded-full bg-navy/8',
  warn:      'mb-3 rounded-token bg-bronze/12 px-3 py-2 text-[12px] text-bronze-text',
  limit:     'mb-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger',
};

export function UsageMeter({ budget }: { budget: AiBudget | null }) {
  if (!budget) return null;
  if (budget.quota == null) {
    return (
      <div className={cls.meterWrap}>
        <div className={cls.meterRow}><span>Assistant usage</span><span>Unlimited</span></div>
      </div>
    );
  }
  const pct = usagePct(budget.used, budget.quota);
  const pctLabel = Math.min(100, Math.round(pct * 100));
  const barColor = pct >= NEAR_LIMIT_AT ? 'bg-danger' : pct >= WARN_AT ? 'bg-bronze' : 'bg-navy';
  return (
    <div className={cls.meterWrap}>
      <div className={cls.meterRow}>
        <span>Assistant usage</span>
        <span>{pctLabel}% of monthly limit</span>
      </div>
      <div className={cls.meterTrack}>
        <div className={`h-full ${barColor} transition-[width] duration-300`} style={{ width: `${Math.min(100, pct * 100)}%` }} />
      </div>
    </div>
  );
}

/** Approaching-cap warning and the hard-limit message. Renders nothing until there is
 *  something to say, so a surface can mount it unconditionally. */
export function UsageNotices({ budget }: { budget: AiBudget | null }) {
  const limitReached = budget != null && !budget.allowed;
  const pct = budget ? usagePct(budget.used, budget.quota) : 0;
  const showWarn = !limitReached && budget?.quota != null && pct >= WARN_AT;

  if (limitReached) {
    return (
      <div className={cls.limit}>
        You&apos;ve reached this month&apos;s assistant limit. It resets at the start of next month.
        An administrator can review usage and plan options in Settings.
      </div>
    );
  }
  if (!showWarn) return null;
  return (
    <div className={cls.warn}>
      {pct >= NEAR_LIMIT_AT
        ? "You're nearly at this month's assistant limit. Usage resets at the start of next month."
        : "You've used most of this month's assistant allowance."}
    </div>
  );
}
